import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft, Share } from "lucide-react";
import { usePermissions } from "../../context/Permissioncontext";
import {
  readUserFromStorage,
  userCanEditPlanningSession,
} from "../../utils/planningAccess";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TrainingSummary from "../../features/trainingSummary/TrainingSummary";
import TrainingSummaryExerciseNav from "../../features/trainingSummary/TrainingSummaryExerciseNav";
import { SessionInfoCard } from "../../components/SessionInfoCard";
import {
  getCurrentHeartRates,
  grpuserlist,
  hrsummary,
  sessionexlist,
  getExerciseFeedback,
} from "../../components/funtionsBD/Mainservice";
import { ip_back } from "../../config";
import { useSubjectPhysiology, buildPlayerOptionPhysiology } from "../../utils/playerPhysiology";
import Loader from "../../components/Loading/Loader";
import {
  summarizeHrPayloadForLog,
  trainingHrDebugHintOnce,
  trainingHrDebugLog,
} from "../../utils/trainingHrDebugLog";
import { formatDuration } from "../../features/trainingSummary/zoneUtils";
import tsStyles from "../../features/trainingSummary/TrainingSummary.module.css";
import {
  resolveTeExecutionType,
  teExecutionTypeLabel,
} from "../../utils/teExecutionType";

/** Coach-only: aggregate HR view for all players with data for this exercise. */
const ALL_PLAYERS_VALUE = "__all_players__";

function formatHrCell(v) {
  if (v == null || !Number.isFinite(Number(v))) return "—";
  return String(Math.round(Number(v)));
}

/** BPM from one hrsummary reading / graph row (API field names vary). */
function parseReadingBpm(d) {
  if (!d || typeof d !== "object") return 0;
  for (const key of ["hr_value", "heart_rate", "hr", "bpm"]) {
    const n = Number(d[key]);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

function parseGraphTimeToSeconds(time) {
  if (time == null) return NaN;
  if (typeof time === "number" && Number.isFinite(time)) return time;
  const s = String(time).trim();
  if (/^\d+(\.\d+)?$/.test(s)) return Number(s);
  const parts = s.split(":").map((p) => parseInt(p, 10));
  if (parts.length === 3 && parts.every((n) => !Number.isNaN(n))) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  return NaN;
}

function buildHrPointsFromInfo(info) {
  if (!info) {
    trainingHrDebugLog("buildHrPointsFromInfo", { branch: "no info" });
    return { hrPoints: [], durationFromSeries: 0 };
  }
  const readings = info.readings;
  if (Array.isArray(readings) && readings.length > 0) {
    const sorted = [...readings].sort((a, b) => {
      const as = Number(a.recorded_sec) || 0;
      const bs = Number(b.recorded_sec) || 0;
      if (as !== bs) return as - bs;
      const ta = a.recorded_at ? new Date(a.recorded_at).getTime() : 0;
      const tb = b.recorded_at ? new Date(b.recorded_at).getTime() : 0;
      return ta - tb;
    });
    const hrPoints = sorted
      .map((d) => ({ bpm: parseReadingBpm(d) }))
      .filter((d) => d.bpm > 0);
    const t0 = Number(sorted[0]?.recorded_sec);
    const t1 = Number(sorted[sorted.length - 1]?.recorded_sec);
    const hasSec =
      Number.isFinite(t0) && Number.isFinite(t1) && sorted.length > 0;
    let durationSec = hasSec ? Math.max(0, t1 - t0) : 0;
    if (durationSec === 0 && sorted.length >= 2) {
      const w0 = sorted[0]?.recorded_at
        ? new Date(sorted[0].recorded_at).getTime()
        : NaN;
      const w1 = sorted[sorted.length - 1]?.recorded_at
        ? new Date(sorted[sorted.length - 1].recorded_at).getTime()
        : NaN;
      if (Number.isFinite(w0) && Number.isFinite(w1) && w1 >= w0) {
        durationSec = Math.round((w1 - w0) / 1000);
      }
    }
    trainingHrDebugLog("buildHrPointsFromInfo", {
      branch: "readings",
      sortedCount: sorted.length,
      hrPointsAfterBpmFilter: hrPoints.length,
      firstRecordedSec: sorted[0]?.recorded_sec,
      lastRecordedSec: sorted[sorted.length - 1]?.recorded_sec,
      firstHr: parseReadingBpm(sorted[0]),
      lastHr: parseReadingBpm(sorted[sorted.length - 1]),
      firstReadingKeys:
        sorted[0] && typeof sorted[0] === "object"
          ? Object.keys(sorted[0])
          : null,
      durationFromSeriesSec: durationSec,
      note:
        hrPoints.length === 0
          ? "All samples dropped: hr_value <= 0 after Number()"
          : durationSec > 0 &&
              (!Number.isFinite(t1 - t0) || (t1 === t0 && sorted.length > 1))
            ? "durationFromSeries used recorded_at wall span (degenerate recorded_sec)"
            : null,
    });
    if (hrPoints.length === 0) {
      const graph = info.hrGraph;
      if (Array.isArray(graph) && graph.length > 0) {
        trainingHrDebugLog("buildHrPointsFromInfo", {
          branch: "readings_all_zero_try_hrGraph",
          readingsCount: sorted.length,
          firstHr: sorted[0]?.hr_value,
        });
      } else {
        return { hrPoints, durationFromSeries: durationSec };
      }
    } else {
      return { hrPoints, durationFromSeries: durationSec };
    }
  }
  const graph = info.hrGraph;
  if (Array.isArray(graph) && graph.length > 0) {
    const pts = [];
    let skippedInvalidTime = 0;
    let skippedNonPositiveHr = 0;
    for (const d of graph) {
      const t = parseGraphTimeToSeconds(d.time);
      const hr = parseReadingBpm(d);
      if (!Number.isFinite(t) || t < 0) {
        skippedInvalidTime += 1;
        continue;
      }
      if (hr <= 0) {
        skippedNonPositiveHr += 1;
        continue;
      }
      pts.push({ bpm: hr, time: t });
    }
    pts.sort((a, b) => a.time - b.time);
    const hrPoints = pts.map((p) => ({ bpm: p.bpm }));
    const durationSec =
      pts.length > 1 ? pts[pts.length - 1].time - pts[0].time : 0;
    trainingHrDebugLog("buildHrPointsFromInfo", {
      branch: "hrGraph",
      graphRowCount: graph.length,
      pointsAfterFilter: pts.length,
      skippedInvalidTime,
      skippedNonPositiveHr,
      firstTime: pts[0]?.time,
      lastTime: pts[pts.length - 1]?.time,
      durationFromSeriesSec: durationSec,
    });
    return { hrPoints, durationFromSeries: durationSec };
  }
  trainingHrDebugLog("buildHrPointsFromInfo", {
    branch: "empty",
    readingsWasArray: Array.isArray(readings),
    readingsLength: Array.isArray(readings) ? readings.length : null,
    graphWasArray: Array.isArray(graph),
    graphLength: Array.isArray(graph) ? graph.length : null,
  });
  return { hrPoints: [], durationFromSeries: 0 };
}

function normalizeAppLang(lng) {
  const base = (lng || "en").split("-")[0];
  if (base === "gr") return "de";
  return base;
}

function getLocalized(obj, prefix, lang) {
  if (!obj) return "";
  return (
    obj[`${prefix}_${lang}`] ||
    obj[`${prefix}_en`] ||
    obj[`${prefix}_fr`] ||
    ""
  );
}

function exerciseDisplayName(ex, lang) {
  if (!ex) return "Exercise";
  if (ex.ex_name) return ex.ex_name;
  const type = getLocalized(ex.type_ex, "type_name", lang);
  const sub = getLocalized(ex.sub_type_ex, "sub_type_name", lang);
  return [type, sub].filter(Boolean).join(" - ") || "Exercise";
}

function sessionStartDateTime(session) {
  const d = session?.ts_date;
  const t = session?.ts_start_time;
  if (!d) return new Date();
  const datePart = String(d).slice(0, 10);
  const timePart = t ? String(t).slice(0, 8) : "12:00:00";
  const parsed = new Date(`${datePart}T${timePart}`);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
}

function getViewerUserId() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}")?.user_id ?? null;
  } catch {
    return null;
  }
}

/**
 * Minutes from `te_duration` (API); invalid → 0
 */
function parseExercisePlannedSeconds(ex) {
  if (!ex) return 0;
  const raw = ex.te_duration ?? ex.te_duration_minutes;
  const n = Number(String(raw ?? "").replace(/,/g, "."));
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 60);
}

function serverStatsFromInfo(info) {
  if (!info || Number(info.maxHR) <= 0) return {};
  return {
    maxHr: Math.round(Number(info.maxHR)),
    minHr:
      Number(info.minHR) > 0 ? Math.round(Number(info.minHR)) : undefined,
    avgHr:
      Number(info.averageHR) > 0
        ? Math.round(Number(info.averageHR))
        : undefined,
  };
}

/**
 * Min/max/avg for ranking: prefer server summary fields, else compute from series.
 */
function extractHrStats(info) {
  const s = serverStatsFromInfo(info);
  if (s.maxHr != null && s.maxHr > 0) {
    return {
      maxHr: s.maxHr,
      minHr: s.minHr != null && s.minHr > 0 ? s.minHr : null,
      avgHr: s.avgHr != null && s.avgHr > 0 ? s.avgHr : null,
    };
  }
  const { hrPoints } = buildHrPointsFromInfo(info);
  if (hrPoints.length === 0) {
    return { maxHr: null, minHr: null, avgHr: null };
  }
  const bpms = hrPoints.map((p) => p.bpm);
  return {
    maxHr: Math.max(...bpms),
    minHr: Math.min(...bpms),
    avgHr: Math.round(bpms.reduce((a, b) => a + b, 0) / bpms.length),
  };
}

function labelCompareRows(a, b) {
  return String(a.label).localeCompare(String(b.label), undefined, {
    sensitivity: "base",
  });
}

function nullMetricSortKey(v) {
  return v == null || !Number.isFinite(Number(v)) ? 1 : 0;
}

/**
 * max: highest max-HR first; min: lowest min-HR first; avg: highest avg-HR first.
 * Failed rows and missing values sort last within each group.
 */
function sortRowsForRanking(rows, metric) {
  const copy = [...rows];
  copy.sort((a, b) => {
    if (a.failed && !b.failed) return 1;
    if (!a.failed && b.failed) return -1;
    if (metric === "max") {
      const am = a.maxHr;
      const bm = b.maxHr;
      const nk = nullMetricSortKey(am) - nullMetricSortKey(bm);
      if (nk !== 0) return nk;
      if (am != null && bm != null && am !== bm) return bm - am;
      return labelCompareRows(a, b);
    }
    if (metric === "min") {
      const am = a.minHr;
      const bm = b.minHr;
      const nk = nullMetricSortKey(am) - nullMetricSortKey(bm);
      if (nk !== 0) return nk;
      if (am != null && bm != null && am !== bm) return am - bm;
      return labelCompareRows(a, b);
    }
    const am = a.avgHr;
    const bm = b.avgHr;
    const nk = nullMetricSortKey(am) - nullMetricSortKey(bm);
    if (nk !== 0) return nk;
    if (am != null && bm != null && am !== bm) return bm - am;
    return labelCompareRows(a, b);
  });
  return copy;
}

function readGrIdFromStorage() {
  try {
    const raw = localStorage.getItem("grpinf");
    if (raw) return JSON.parse(raw)?.groupDetails?.gr_id ?? null;
  } catch {
    /* ignore */
  }
  return null;
}

function readGrpFromStorage() {
  try {
    const raw = localStorage.getItem("grpinf");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.groupDetails || parsed?.gr_id) return parsed;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function readSessionFromStorage() {
  try {
    const raw = localStorage.getItem("rsessn");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.ts_id) return parsed;
    }
  } catch {
    /* ignore */
  }
  return null;
}

/** playerAndCoachgroupList uses id=user_id, firstName/lastName; tolerate other shapes. */
function displayNameFromGroupListPlayer(p) {
  if (!p || typeof p !== "object") return null;
  const alias = String(p.user_alias || "").trim();
  const fn = String(p.firstName || p.user_first_name || "").trim();
  const ln = String(p.lastName || p.user_last_name || "").trim();
  const full = [fn, ln].filter(Boolean).join(" ").trim();
  if (alias) return alias;
  if (full) return full;
  if (fn) return fn;
  return null;
}

/** getCurrentHeartRates rows may include player_display_name / user_* from tbl_user join. */
function displayNameFromHrCurrentRow(r) {
  if (!r || typeof r !== "object") return null;
  const d = String(r.player_display_name || "").trim();
  if (d) return d;
  const alias = String(r.user_alias || "").trim();
  if (alias) return alias;
  const fn = String(r.user_first_name || "").trim();
  const ln = String(r.user_last_name || "").trim();
  const full = [fn, ln].filter(Boolean).join(" ").trim();
  if (full) return full;
  if (fn) return fn;
  return null;
}

export default function TrainingSummaryPage() {
  const { t, i18n } = useTranslation();
  const { permissions } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    trainingHrDebugHintOnce();
  }, []);
  const selectedSession = useMemo(() => {
    const fromState = location.state?.selectedSession;
    if (fromState?.ts_id) return fromState;
    return readSessionFromStorage();
  }, [location.state?.selectedSession]);

  const selectedgrp = useMemo(() => {
    const fromState = location.state?.selectedgrp;
    if (fromState?.groupDetails || fromState?.gr_id) return fromState;
    return readGrpFromStorage();
  }, [location.state?.selectedgrp]);

  const exercise = location.state?.exercise;
  const targetUserId = location.state?.targetUserId;
  const subjectProfile = location.state?.subjectProfile;
  const lang = normalizeAppLang(i18n.language);

  const [sessionExercises, setSessionExercises] = useState([]);
  const [sessionExercisesLoading, setSessionExercisesLoading] = useState(false);

  const [userRefresh, setUserRefresh] = useState(0);
  useEffect(() => {
    const onUser = () => setUserRefresh((n) => n + 1);
    window.addEventListener("smarttt-user-updated", onUser);
    return () => window.removeEventListener("smarttt-user-updated", onUser);
  }, []);

  /** Staff (coach/club admin/…) may pick any player with HR; pure players only see their own data. */
  const canSelectPlayersHr = useMemo(
    () => userCanEditPlanningSession(readUserFromStorage(), permissions),
    [permissions, userRefresh]
  );
  const isPlayer = !canSelectPlayersHr;
  const resolvedGrId =
    location.state?.selectedgrp?.groupDetails?.gr_id ??
    selectedSession?.gr_id ??
    readGrIdFromStorage();
  const teId = exercise?.te_id;

  useEffect(() => {
    if (!selectedSession?.ts_id) {
      setSessionExercises([]);
      setSessionExercisesLoading(false);
      return;
    }
    let cancelled = false;
    const run = async () => {
      setSessionExercisesLoading(true);
      try {
        const res = await sessionexlist({ ts_id: selectedSession.ts_id });
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        list.sort((a, b) => (Number(a.te_order) || 0) - (Number(b.te_order) || 0));
        if (!cancelled) setSessionExercises(list);
      } catch {
        if (!cancelled) setSessionExercises([]);
      } finally {
        if (!cancelled) setSessionExercisesLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [selectedSession?.ts_id]);

  const [playerOptions, setPlayerOptions] = useState([]);
  const [playersLoading, setPlayersLoading] = useState(!isPlayer);
  const [selectedUserId, setSelectedUserId] = useState(() => {
    if (isPlayer) {
      const v = getViewerUserId();
      return v != null ? String(v) : null;
    }
    if (targetUserId != null) return String(targetUserId);
    return null;
  });

  const navigateToExercise = useCallback(
    (nextExercise) => {
      if (!nextExercise?.te_id) return;
      const opt = playerOptions.find((o) => o.value === selectedUserId);
      const coachTarget =
        !isPlayer && selectedUserId && selectedUserId !== ALL_PLAYERS_VALUE
          ? {
              targetUserId: opt?.userId || selectedUserId,
            }
          : targetUserId != null
            ? { targetUserId }
            : {};
      navigate("/Training_Summary", {
        state: {
          selectedSession,
          selectedgrp,
          exercise: nextExercise,
          ...coachTarget,
          subjectProfile,
        },
        replace: true,
      });
    },
    [
      navigate,
      selectedSession,
      selectedgrp,
      playerOptions,
      selectedUserId,
      isPlayer,
      targetUserId,
      subjectProfile,
    ]
  );

  const sessionContextSlot = useMemo(() => {
    if (!selectedSession?.ts_id) return null;
    return (
      <div className={tsStyles.sessionContextStack}>
        <SessionInfoCard
          selectedSession={selectedSession}
          selectedgrp={selectedgrp}
        />
        {teId ? (
          <TrainingSummaryExerciseNav
            exercises={sessionExercises}
            currentTeId={teId}
            currentLabel={exerciseDisplayName(exercise, lang)}
            onSelectExercise={navigateToExercise}
            loading={sessionExercisesLoading}
          />
        ) : null}
      </div>
    );
  }, [
    selectedSession,
    selectedgrp,
    teId,
    sessionExercises,
    sessionExercisesLoading,
    exercise,
    lang,
    navigateToExercise,
  ]);

  const [loadingHr, setLoadingHr] = useState(true);
  const [hrInfo, setHrInfo] = useState(null);
  const [evaluationVideoUrl, setEvaluationVideoUrl] = useState(null);

  const [allPlayersRows, setAllPlayersRows] = useState(null);
  const [allPlayersLoading, setAllPlayersLoading] = useState(false);
  const [allPlayersError, setAllPlayersError] = useState(null);

  useEffect(() => {
    if (isPlayer) {
      setPlayersLoading(false);
      return;
    }
    if (!teId || !selectedSession?.ts_id) {
      setPlayerOptions([]);
      setPlayersLoading(false);
      return;
    }

    const run = async (withGrList) => {
      try {
        let playersForLabels = [];
        let byPlayerId = new Map();
        let byUserId = new Map();

        if (withGrList && resolvedGrId) {
          try {
            const plRes = await grpuserlist({
              ts_id: selectedSession.ts_id,
              gr_id: resolvedGrId,
            });
            playersForLabels = (plRes.data?.data || []).filter(
              (p) => p.role === "player"
            );
            byPlayerId = new Map(
              playersForLabels
                .filter(
                  (p) =>
                    p.player_id != null && String(p.player_id).trim() !== ""
                )
                .map((p) => [String(p.player_id), p])
            );
            byUserId = new Map(
              playersForLabels.map((p) => [String(p.id), p])
            );
          } catch (e) {
            console.error("Error loading group user list", e);
          }
        }

        const hrRes = await getCurrentHeartRates({ te_id: teId });
        const hrRows = hrRes.data?.data || [];
        trainingHrDebugLog("TrainingSummaryPage getCurrentHeartRates", {
          te_id: teId,
          rowCount: hrRows.length,
          hr_samples_total: hrRes.data?.hr_samples_total,
          hr_samples_positive: hrRes.data?.hr_samples_positive,
          picker_source: hrRes.data?.picker_source,
          sampleRows: hrRows.slice(0, 5),
        });

        // Form the union of players from HR data and group participants
        const hrKeys = hrRows
          .map((r) => {
            const pid =
              r.player_id != null && String(r.player_id).trim() !== ""
                ? String(r.player_id)
                : null;
            const uid = r.user_id != null ? String(r.user_id) : null;
            return pid || uid;
          })
          .filter(Boolean);

        const groupKeys = playersForLabels
          .map((p) => {
            const pid =
              p.player_id != null && String(p.player_id).trim() !== ""
                ? String(p.player_id)
                : null;
            const uid = p.id != null ? String(p.id) : null;
            return pid || uid;
          })
          .filter(Boolean);

        let keys = [...new Set([...hrKeys, ...groupKeys])];

        const rowForKey = (value) =>
          hrRows.find((r) => {
            const pid =
              r.player_id != null && String(r.player_id).trim() !== ""
                ? String(r.player_id)
                : null;
            const uid = r.user_id != null ? String(r.user_id) : null;
            return (pid || uid) === value;
          });

        const buildOptionsFromKeys = (keyList) =>
          keyList.map((value) => {
            const fromHrRow = rowForKey(value);
            const pid =
              fromHrRow?.player_id != null &&
              String(fromHrRow.player_id).trim() !== ""
                ? String(fromHrRow.player_id)
                : null;
            const uid =
              fromHrRow?.user_id != null ? String(fromHrRow.user_id) : null;
            const p =
              (pid && byPlayerId.get(pid)) ||
              (uid && byUserId.get(uid)) ||
              byPlayerId.get(value) ||
              byUserId.get(value) ||
              null;
            const name =
              displayNameFromHrCurrentRow(fromHrRow) ||
              displayNameFromGroupListPlayer(p);
            const { physiology, hasDob } = buildPlayerOptionPhysiology(
              fromHrRow,
              p
            );
            return {
              value,
              playerId: pid || (p?.player_id != null ? String(p.player_id) : null),
              userId: uid || (p?.id != null ? String(p.id) : null),
              label: name || t("player") + " " + String(value).slice(-6),
              physiology,
              hasDob,
            };
          });

        let options = buildOptionsFromKeys(keys);

        // Deduplicate option objects just in case
        const uniqueOptionsMap = new Map();
        options.forEach(opt => {
          uniqueOptionsMap.set(opt.value, opt);
        });
        options = Array.from(uniqueOptionsMap.values());

        setPlayerOptions(options);
        setSelectedUserId((prev) => {
          if (options.length === 0) return null;
          if (prev && options.some((o) => o.value === prev)) return prev;
          if (targetUserId != null) {
            const match = options.find(
              (o) =>
                String(o.userId || "") === String(targetUserId) ||
                String(o.value) === String(targetUserId)
            );
            if (match) return match.value;
          }
          return options[0].value;
        });
      } catch {
        setPlayerOptions([]);
        setSelectedUserId(null);
      } finally {
        setPlayersLoading(false);
      }
    };

    setPlayersLoading(true);
    if (resolvedGrId) {
      run(true);
    } else {
      run(false);
    }
  }, [
    isPlayer,
    teId,
    selectedSession?.ts_id,
    resolvedGrId,
    targetUserId,
    t,
  ]);

  const loadHr = useCallback(async () => {
    if (!teId || !selectedUserId) {
      setHrInfo(null);
      setLoadingHr(false);
      return;
    }
    if (selectedUserId === ALL_PLAYERS_VALUE) {
      setHrInfo(null);
      setLoadingHr(false);
      return;
    }
    setLoadingHr(true);
    try {
      const opt = playerOptions.find((o) => o.value === selectedUserId);
      const summaryBody =
        opt?.playerId != null && String(opt.playerId).trim() !== ""
          ? { te_id: teId, player_id: String(opt.playerId) }
          : { te_id: teId, user_id: opt?.userId || selectedUserId };
      trainingHrDebugLog("TrainingSummaryPage hrsummary request", summaryBody);
      const res = await hrsummary(summaryBody);
      const envelope = res?.data;
      const body = envelope?.data ?? null;
      trainingHrDebugLog("TrainingSummaryPage hrsummary response", {
        apiStatus: envelope?.status,
        summary: summarizeHrPayloadForLog(body),
        fullReadingsCount: Array.isArray(body?.readings)
          ? body.readings.length
          : 0,
      });
      setHrInfo(body);
    } catch (err) {
      const errBody = err?.response?.data ?? err;
      trainingHrDebugLog("TrainingSummaryPage hrsummary error", {
        message: errBody?.message ?? err?.message,
        hr_rejection_reason: errBody?.hr_rejection_reason,
        te_id: teId,
        selectedValue: selectedUserId,
      });
      setHrInfo(null);
    } finally {
      setLoadingHr(false);
    }
  }, [teId, selectedUserId, playerOptions]);

  useEffect(() => {
    if (!isPlayer && playersLoading) return;
    if (!teId || !selectedUserId) {
      setHrInfo(null);
      setLoadingHr(false);
      return;
    }
    if (selectedUserId === ALL_PLAYERS_VALUE) {
      setHrInfo(null);
      setLoadingHr(false);
      return;
    }
    loadHr();
  }, [teId, selectedUserId, isPlayer, playersLoading, loadHr]);

  useEffect(() => {
    if (!teId || !selectedUserId || selectedUserId === ALL_PLAYERS_VALUE) {
      setEvaluationVideoUrl(null);
      return;
    }
    const fetchVideo = async () => {
      try {
        const opt = playerOptions.find((o) => o.value === selectedUserId);
        const targetPlayerUserId = opt?.userId || selectedUserId;
        const res = await getExerciseFeedback({ te_id: teId });
        const feedbacks = res?.data?.data?.feedbacks || [];
        
        // Find the video message where sender or receiver is the selected player
        const videoMsg = feedbacks.find((fb) => {
          if (fb.message_type !== "video" && fb.message_type !== "evaluation") return false;
          if (!fb.video_message_url) return false;
          const targetUidStr = String(targetPlayerUserId);
          return String(fb.sender_id) === targetUidStr || String(fb.receiver_id) === targetUidStr;
        });

        if (videoMsg?.video_message_url) {
          const url = videoMsg.video_message_url;
          const absoluteUrl = url.startsWith("http")
            ? url
            : `${ip_back.replace(/\/$/, "")}/${url.replace(/^\//, "")}`;
          setEvaluationVideoUrl(absoluteUrl);
        } else {
          setEvaluationVideoUrl(null);
        }
      } catch (err) {
        console.error("Error loading evaluation video in TrainingSummaryPage:", err);
        setEvaluationVideoUrl(null);
      }
    };
    fetchVideo();
  }, [teId, selectedUserId, playerOptions]);

  const loadAllPlayerRankings = useCallback(async () => {
    if (!teId || playerOptions.length === 0) {
      setAllPlayersRows([]);
      return;
    }
    setAllPlayersLoading(true);
    setAllPlayersError(null);
    try {
      const settled = await Promise.allSettled(
        playerOptions.map((opt) =>
          hrsummary(
            opt.playerId != null && String(opt.playerId).trim() !== ""
              ? { te_id: teId, player_id: String(opt.playerId) }
              : { te_id: teId, user_id: opt.userId || opt.value }
          )
        )
      );
      const rows = playerOptions.map((opt, i) => {
        const s = settled[i];
        if (s.status === "rejected") {
          return {
            userId: opt.userId || opt.value,
            subjectKey: opt.value,
            label: opt.label,
            maxHr: null,
            minHr: null,
            avgHr: null,
            failed: true,
          };
        }
        const body = s.value?.data?.data ?? null;
        const stats = extractHrStats(body);
        return {
          userId: opt.userId || opt.value,
          subjectKey: opt.value,
          label: opt.label,
          ...stats,
          failed: false,
        };
      });
      setAllPlayersRows(rows);
    } catch (e) {
      setAllPlayersError(e);
      setAllPlayersRows(null);
    } finally {
      setAllPlayersLoading(false);
    }
  }, [teId, playerOptions]);

  useEffect(() => {
    if (isPlayer || playersLoading) return;
    if (selectedUserId !== ALL_PLAYERS_VALUE) {
      setAllPlayersRows(null);
      setAllPlayersError(null);
      return;
    }
    if (!teId) return;
    loadAllPlayerRankings();
  }, [
    isPlayer,
    playersLoading,
    selectedUserId,
    teId,
    loadAllPlayerRankings,
  ]);

  const rankedByMaxHr = useMemo(
    () =>
      allPlayersRows?.length
        ? sortRowsForRanking(allPlayersRows, "max")
        : [],
    [allPlayersRows]
  );
  const rankedByMinHr = useMemo(
    () =>
      allPlayersRows?.length
        ? sortRowsForRanking(allPlayersRows, "min")
        : [],
    [allPlayersRows]
  );
  const rankedByAvgHr = useMemo(
    () =>
      allPlayersRows?.length
        ? sortRowsForRanking(allPlayersRows, "avg")
        : [],
    [allPlayersRows]
  );

  const selectedPlayerOption = useMemo(
    () => playerOptions.find((o) => o.value === selectedUserId),
    [playerOptions, selectedUserId]
  );

  const viewSubjectUserId = useMemo(() => {
    if (isPlayer) return null;
    if (!selectedUserId || selectedUserId === ALL_PLAYERS_VALUE) return null;
    if (selectedPlayerOption?.userId) return String(selectedPlayerOption.userId);
    if (
      targetUserId != null &&
      String(selectedUserId) === String(targetUserId)
    ) {
      return String(targetUserId);
    }
    return null;
  }, [isPlayer, selectedUserId, selectedPlayerOption, targetUserId]);

  const viewSubjectPlayerId = useMemo(() => {
    if (isPlayer || viewSubjectUserId) return null;
    if (!selectedUserId || selectedUserId === ALL_PLAYERS_VALUE) return null;
    return selectedPlayerOption?.playerId || selectedUserId;
  }, [isPlayer, viewSubjectUserId, selectedUserId, selectedPlayerOption]);

  const { physiology } = useSubjectPhysiology({
    viewUserId: viewSubjectUserId,
    viewPlayerId: viewSubjectPlayerId,
    subjectProfile,
    useStorageFallback: isPlayer,
    inlinePhysiology: selectedPlayerOption?.physiology,
    inlineHasDob: !!selectedPlayerOption?.hasDob,
  });

  const { hrPoints, durationFromSeries } = useMemo(
    () => buildHrPointsFromInfo(hrInfo),
    [hrInfo]
  );

  const durationSec = useMemo(() => {
    const planned = parseExercisePlannedSeconds(exercise);
    const span = Number.isFinite(durationFromSeries) && durationFromSeries >= 0
      ? durationFromSeries
      : 0;
    const summaryApi = Number(hrInfo?.summary_duration_sec);
    const winApi = Number(hrInfo?.hr_active_window_sec);
    const coverageApi = Number(hrInfo?.strict_active_coverage_sec);
    const durationSource = hrInfo?.summary_duration_source;

    let pickApi = null;
    if (Number.isFinite(summaryApi) && summaryApi > 0) {
      pickApi = Math.round(summaryApi);
    } else if (Number.isFinite(winApi) && winApi > 0) {
      pickApi = Math.round(winApi);
    }

    if (pickApi != null) {
      const capped =
        Number.isFinite(span) && span > 0
          ? Math.min(pickApi, Math.round(span))
          : pickApi;
      trainingHrDebugLog("TrainingSummaryPage durationSec", {
        source: "summary_duration_sec / hr_active_window_sec (API)",
        summary_duration_source: durationSource,
        seconds: capped,
        summary_duration_sec_raw: summaryApi,
        hr_active_window_sec_raw: winApi,
        strict_active_coverage_sec: coverageApi,
        durationFromSeriesSec: span,
        plannedExerciseSeconds: planned,
        hrPointsCount: hrPoints.length,
      });
      return capped;
    }

    if (hrPoints.length > 0 && span > 0) {
      trainingHrDebugLog("TrainingSummaryPage durationSec", {
        source: "durationFromSeries (rebased readings)",
        seconds: Math.round(span),
        plannedExerciseSeconds: planned,
      });
      return Math.round(span);
    }

    const activeSpanApi = Number(hrInfo?.active_span_sec);
    if (
      span > 0 &&
      (hrPoints.length > 0 ||
        (Number.isFinite(activeSpanApi) && activeSpanApi > 0) ||
        (Number(hrInfo?.maxHR) > 0 && Number(hrInfo?.averageHR) > 0))
    ) {
      const fromApi =
        Number.isFinite(activeSpanApi) && activeSpanApi > 0
          ? Math.round(activeSpanApi)
          : span;
      trainingHrDebugLog("TrainingSummaryPage durationSec", {
        source: "active_span_sec / readings wall span (BPM missing in rows)",
        seconds: fromApi,
        durationFromSeriesSec: span,
        active_span_sec: activeSpanApi,
        maxHR: hrInfo?.maxHR,
        plannedExerciseSeconds: planned,
      });
      return fromApi;
    }

    trainingHrDebugLog("TrainingSummaryPage durationSec", {
      source: "no HR duration",
      plannedExerciseSeconds: planned,
      hrPointsCount: hrPoints.length,
    });
    return 0;
  }, [hrInfo, exercise, durationFromSeries, hrPoints.length]);

  const serverStats = useMemo(() => serverStatsFromInfo(hrInfo), [hrInfo]);

  const exercisePayload = useMemo(
    () => ({
      name: exerciseDisplayName(exercise, lang),
      typeCategory: getLocalized(exercise?.type_ex, "type_name", lang) || undefined,
      subtype:
        getLocalized(exercise?.sub_type_ex, "sub_type_name", lang) || undefined,
      executionModeLabel: teExecutionTypeLabel(
        resolveTeExecutionType(exercise),
        t
      ),
      dateTime: sessionStartDateTime(selectedSession),
      durationSec,
      durationLabel: formatDuration(durationSec),
    }),
    [exercise, selectedSession, lang, durationSec, t]
  );

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleShare = useCallback(async () => {
    const title = exercisePayload.name;
    const text = `${title} — ${exercisePayload.durationLabel}`;
    if (navigator.share) {
      try {
        await navigator.share({ title, text });
        return;
      } catch {
        /* user cancelled or share failed */
      }
    }
    window.print();
  }, [exercisePayload]);

  const selectOptions = useMemo(
    () =>
      playerOptions.length > 0
        ? [
            {
              value: ALL_PLAYERS_VALUE,
              label: t("training_summary_all_players") || "All players",
            },
            ...playerOptions,
          ]
        : [],
    [playerOptions, t]
  );

  const isAllPlayers = selectedUserId === ALL_PLAYERS_VALUE;

  const showCoachPicker = !isPlayer && playerOptions.length > 0;

  const topSlot = showCoachPicker ? (
    <div className={tsStyles.coachPlayerSelect}>
      <label htmlFor="training-summary-player-select">
        {t("selectPlayer") || "Select Player"}
      </label>
      <FormControl fullWidth size="small">
        <InputLabel id="training-summary-player-label">
          {t("selectPlayer") || "Player"}
        </InputLabel>
        <Select
          labelId="training-summary-player-label"
          id="training-summary-player-select"
          value={selectedUserId || ""}
          label={t("selectPlayer") || "Player"}
          onChange={(e) => setSelectedUserId(String(e.target.value))}
        >
          {selectOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <p className={tsStyles.coachPlayerSelectHint}>
        {t("training_summary_coach_picker_hint_all") ||
          "Select any session participant to review their summary, metrics, and video evaluation."}
      </p>
    </div>
  ) : null;

  if (!teId) {
    return (
      <div className={tsStyles.fullPageShellPadded}>
        <p className={tsStyles.shellMessage}>
          {t("training_summary_missing") || "No exercise selected."}
        </p>
      </div>
    );
  }

  if (isPlayer && !getViewerUserId()) {
    return (
      <div className={tsStyles.fullPageShell}>
        <p className={tsStyles.shellMessage}>
          {t("notAuthorized") || "Not signed in."}
        </p>
      </div>
    );
  }

  if (!isPlayer && playersLoading) {
    return (
      <div className={tsStyles.fullPageShell}>
        <Loader />
      </div>
    );
  }

  if (!isPlayer && !playersLoading && playerOptions.length === 0) {
    return (
      <div className={tsStyles.fullPageStack} style={{ alignItems: "stretch", maxWidth: 480, margin: "0 auto", width: "100%" }}>
        {sessionContextSlot}
        <p className={tsStyles.shellMessage}>
          {t("training_hr_none_captured") ||
            "No heart rate data for this exercise. Players must have HR samples recorded."}
        </p>
        <button
          type="button"
          onClick={handleBack}
          className={tsStyles.primaryCta}
        >
          {t("back") || "Back"}
        </button>
      </div>
    );
  }

  if (
    isAllPlayers &&
    (allPlayersLoading ||
      (allPlayersRows == null && allPlayersError == null))
  ) {
    return (
      <div className={tsStyles.fullPageShell}>
        <Loader />
      </div>
    );
  }

  if (loadingHr && !isAllPlayers) {
    return (
      <div className={tsStyles.fullPageShell}>
        <Loader />
      </div>
    );
  }

  if (
    isAllPlayers &&
    !allPlayersLoading &&
    (allPlayersRows != null || allPlayersError != null)
  ) {
    const dateText = sessionStartDateTime(selectedSession).toLocaleString(
      undefined,
      {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }
    );
    return (
      <div className={tsStyles.page}>
        <div className={tsStyles.container}>
          {sessionContextSlot}
          {topSlot}
          <div className={tsStyles.header}>
            <button
              type="button"
              className={tsStyles.iconBtn}
              onClick={handleBack}
              aria-label="Back"
            >
              <ChevronLeft size={24} />
            </button>
            <h1 className={tsStyles.title}>{t("summary")}</h1>
            <button
              type="button"
              className={tsStyles.iconBtn}
              onClick={handleShare}
              aria-label="Share"
            >
              <Share size={22} />
            </button>
          </div>

          <div className={`${tsStyles.card} ${tsStyles.summaryCard}`}>
            <h2 className={tsStyles.summaryTitle}>{exercisePayload.name}</h2>
            {exercisePayload.typeCategory && (
              <p className={tsStyles.summaryType}>
                {exercisePayload.typeCategory}
              </p>
            )}
            {exercisePayload.executionModeLabel && (
              <p className={tsStyles.summaryType}>
                {exercisePayload.executionModeLabel}
              </p>
            )}
            {exercisePayload.subtype && (
              <p className={tsStyles.summarySubtype}>
                {exercisePayload.subtype}
              </p>
            )}
            <p className={tsStyles.summaryDate}>{dateText}</p>
          </div>

          <div
            className={`${tsStyles.card} ${tsStyles.rankingGroupCard}`}
            style={{ marginTop: 24 }}
          >
            <h3 className={tsStyles.rankingTitle}>
              {t("training_summary_hr_ranking_title") || "Heart rate ranking"}
            </h3>
            {allPlayersError != null && (
              <p className={tsStyles.rankingError}>
                {t("training_hr_load_error") ||
                  "Could not load heart rate data. Try again or pick a single player."}
              </p>
            )}
            {allPlayersError == null &&
              allPlayersRows != null &&
              allPlayersRows.length === 0 && (
              <p className={tsStyles.chartEmpty} style={{ padding: 24 }}>
                {t("hr_nodata") || "No HR data"}
              </p>
            )}
            {allPlayersError == null &&
              allPlayersRows != null &&
              allPlayersRows.length > 0 && (
                <>
                  {[
                    {
                      k: "max",
                      badge: "MAX",
                      title: t("training_summary_hr_table_max"),
                      col: t("training_hr_max"),
                      rows: rankedByMaxHr,
                      val: (r) => r.maxHr,
                      sectionClass: tsStyles.rankingSectionMax,
                    },
                    {
                      k: "min",
                      badge: "MIN",
                      title: t("training_summary_hr_table_min"),
                      col: t("training_hr_min"),
                      rows: rankedByMinHr,
                      val: (r) => r.minHr,
                      sectionClass: tsStyles.rankingSectionMin,
                    },
                    {
                      k: "avg",
                      badge: "AVG",
                      title: t("training_summary_hr_table_avg"),
                      col: t("training_hr_avg"),
                      rows: rankedByAvgHr,
                      val: (r) => r.avgHr,
                      sectionClass: tsStyles.rankingSectionAvg,
                    },
                  ].map((block, i) => (
                    <div
                      key={block.k}
                      className={[
                        tsStyles.rankingTableSection,
                        block.sectionClass,
                        i === 0
                          ? tsStyles.rankingTableBlockFirst
                          : tsStyles.rankingTableBlock,
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <div className={tsStyles.rankingTableHeaderRow}>
                        <span
                          className={tsStyles.rankingTableIconBadge}
                          aria-hidden
                        >
                          {block.badge}
                        </span>
                        <h4 className={tsStyles.rankingTableSubtitle}>
                          {block.title}
                        </h4>
                      </div>
                      <div className={tsStyles.rankingTableWrap}>
                        <table className={tsStyles.rankingTable}>
                          <thead>
                            <tr className={tsStyles.rankingTheadRow}>
                              <th
                                className={tsStyles.rankingTheadRank}
                                scope="col"
                                aria-label={
                                  t("training_summary_hr_rank_col") || "Rank"
                                }
                              >
                                #
                              </th>
                              <th
                                className={tsStyles.rankingTheadName}
                                scope="col"
                              >
                                {t("player")}
                              </th>
                              <th scope="col" className={tsStyles.rankingNumCol}>
                                {block.col}
                              </th>
                            </tr>
                          </thead>
                          <tbody className={tsStyles.rankingTbody}>
                            {block.rows.map((row, idx) => (
                              <tr
                                key={`${block.k}-${row.subjectKey}`}
                                className={[
                                  tsStyles.rankingDataRow,
                                  idx === 0 && tsStyles.rankingDataRowTop1,
                                  idx === 1 && tsStyles.rankingDataRowTop2,
                                  idx === 2 && tsStyles.rankingDataRowTop3,
                                ]
                                  .filter(Boolean)
                                  .join(" ")}
                              >
                                <td className={tsStyles.rankingRankCell}>
                                  <span
                                    className={[
                                      tsStyles.rankingRankBadge,
                                      idx === 0 && tsStyles.rankingMedal1,
                                      idx === 1 && tsStyles.rankingMedal2,
                                      idx === 2 && tsStyles.rankingMedal3,
                                    ]
                                      .filter(Boolean)
                                      .join(" ")}
                                  >
                                    {idx + 1}
                                  </span>
                                </td>
                                <td
                                  className={tsStyles.rankingName}
                                  title={row.label}
                                >
                                  {row.label}
                                  {row.failed ? " *" : ""}
                                </td>
                                <td className={tsStyles.rankingValueCell}>
                                  <span className={tsStyles.rankingValuePill}>
                                    {formatHrCell(block.val(row))}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </>
              )}
            {allPlayersError == null &&
              allPlayersRows != null &&
              allPlayersRows.some((r) => r.failed) && (
              <p className={tsStyles.coachPlayerSelectHint} style={{ marginTop: 8 }}>
                * {t("training_hr_load_error") || "Could not load this player."}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <TrainingSummary
      sessionContextSlot={sessionContextSlot}
      topSlot={topSlot}
      exercise={exercisePayload}
      hrPoints={hrPoints}
      user={physiology}
      serverStats={serverStats}
      onBack={handleBack}
      onShare={handleShare}
      videoUrl={evaluationVideoUrl}
    />
  );
}
